/* ─── Backup sync (lazy-loaded to avoid circular dependency) ─── */
let _triggerBackupSync = null;
function lazyBackupSync(key, userId) {
  if (!userId) return;
  if (!_triggerBackupSync) {
    import('../services/backupService').then((mod) => {
      _triggerBackupSync = mod.triggerBackupSync;
      _triggerBackupSync(key, userId);
    }).catch(() => {}); // silent fail — backup is non-critical
    return;
  }
  _triggerBackupSync(key, userId);
}

let _backupUserId = null;
export function setBackupUserId(userId) { _backupUserId = userId; }

const KEYS = {
  GARAGES: 'pm_garages',
  VEHICLES: 'pm_vehicles',
  ANALYSES: 'pm_analyses',
  SERVICE_HISTORY: 'pm_service_history',
  CHAT_HISTORIES: 'pm_chat_histories',
  AA_ANALYSES: 'pm_aa_analyses',
  SETTINGS: 'pm_settings',
  USER_PROFILE: 'pm_user_profile',
  MILEAGE_LOGS: 'pm_mileage_logs',
  DISMISSED_ITEMS: 'pm_dismissed_items',
  DIY_GUIDES: 'pm_diy_guides',
  AA_CHECKLIST: 'pm_aa_checklist',
  VALUE_HISTORY: 'pm_value_history',
  REMINDERS: 'pm_reminders',
  CLUB_MEMBERSHIPS: 'pm_club_memberships',
  CACHED_EVENTS: 'pm_cached_events',
};

/* ───────────────────── Garages ───────────────────── */

export function getGarages() {
  const raw = localStorage.getItem(KEYS.GARAGES);
  return raw ? JSON.parse(raw) : [];
}

export function saveGarages(garages) {
  localStorage.setItem(KEYS.GARAGES, JSON.stringify(garages));
}

export function addGarage({ name, photo = null, location = '' }) {
  const garages = getGarages();
  const garage = {
    id: `garage_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    name,
    photo,
    vehicleIds: [],
    location,
    owner: 'carsten@propermiles.com',
    collaborators: [],
  };
  garages.push(garage);
  saveGarages(garages);
  return garage;
}

export function upsertGarage(garageData) {
  const garages = getGarages();
  const idx = garages.findIndex((g) => g.id === garageData.id);
  if (idx !== -1) {
    garages[idx] = { ...garages[idx], ...garageData };
  } else {
    garages.push(garageData);
  }
  saveGarages(garages);
  return garageData;
}

export function updateGarage(id, fields) {
  const garages = getGarages();
  const idx = garages.findIndex((g) => g.id === id);
  if (idx === -1) return;
  garages[idx] = { ...garages[idx], ...fields };
  saveGarages(garages);
  return garages[idx];
}

export function removeGarage(id) {
  let garages = getGarages();
  const garage = garages.find((g) => g.id === id);
  if (!garage) return;

  // Remove all vehicles in this garage
  garage.vehicleIds.forEach((vid) => {
    removeVehicle(vid, true); // skipGarageUpdate — garage is being deleted
  });

  garages = garages.filter((g) => g.id !== id);
  saveGarages(garages);
  return garages;
}

/* ───────────────────── Vehicles ───────────────────── */

export function getVehicles() {
  const raw = localStorage.getItem(KEYS.VEHICLES);
  return raw ? JSON.parse(raw) : [];
}

export function getVehiclesByGarage(garageId) {
  return getVehicles().filter((v) => v.garageId === garageId);
}

export function getVehicle(vehicleId) {
  return getVehicles().find((v) => v.id === vehicleId) || null;
}

export function saveVehicles(vehicles) {
  localStorage.setItem(KEYS.VEHICLES, JSON.stringify(vehicles));
}

/**
 * Upsert a vehicle. If vehicle.id exists in the array, update it.
 * Otherwise, append it and add its id to the garage's vehicleIds.
 */
export function saveVehicle(vehicleData) {
  const vehicles = getVehicles();
  const idx = vehicles.findIndex((v) => v.id === vehicleData.id);

  if (idx !== -1) {
    // Update existing
    vehicles[idx] = { ...vehicles[idx], ...vehicleData };
  } else {
    // New vehicle — append
    vehicles.push(vehicleData);
    // Add to garage's vehicleIds
    if (vehicleData.garageId) {
      const garages = getGarages();
      const garage = garages.find((g) => g.id === vehicleData.garageId);
      if (garage && !garage.vehicleIds.includes(vehicleData.id)) {
        garage.vehicleIds.push(vehicleData.id);
        saveGarages(garages);
      }
    }
  }

  saveVehicles(vehicles);
  return vehicleData;
}

export function removeVehicle(vehicleId, skipGarageUpdate = false) {
  let vehicles = getVehicles();
  const vehicle = vehicles.find((v) => v.id === vehicleId);
  if (!vehicle) return;

  // Remove from vehicles array
  vehicles = vehicles.filter((v) => v.id !== vehicleId);
  saveVehicles(vehicles);

  // Remove from garage's vehicleIds
  if (!skipGarageUpdate && vehicle.garageId) {
    const garages = getGarages();
    const garage = garages.find((g) => g.id === vehicle.garageId);
    if (garage) {
      garage.vehicleIds = garage.vehicleIds.filter((id) => id !== vehicleId);
      saveGarages(garages);
    }
  }

  // Remove analysis
  const analyses = getAllAnalyses();
  delete analyses[vehicleId];
  localStorage.setItem(KEYS.ANALYSES, JSON.stringify(analyses));

  // Remove service history entries for this vehicle
  const sh = getServiceHistory();
  const filtered = sh.filter((e) => e.vehicleId !== vehicleId);
  saveServiceHistory(filtered);

  // Remove chat history
  const chats = getAllChatHistories();
  delete chats[vehicleId];
  localStorage.setItem(KEYS.CHAT_HISTORIES, JSON.stringify(chats));
  lazyBackupSync(KEYS.CHAT_HISTORIES, _backupUserId);

  // Remove reminders for this vehicle
  const reminders = getReminders();
  const filteredReminders = reminders.filter((r) => r.vehicleId !== vehicleId);
  saveReminders(filteredReminders);
}

export function moveVehicle(vehicleId, toGarageId) {
  const vehicles = getVehicles();
  const vehicle = vehicles.find((v) => v.id === vehicleId);
  if (!vehicle) return;

  const garages = getGarages();
  const fromGarage = garages.find((g) => g.id === vehicle.garageId);
  const toGarage = garages.find((g) => g.id === toGarageId);
  if (!toGarage) return;

  // Remove from old garage
  if (fromGarage) {
    fromGarage.vehicleIds = fromGarage.vehicleIds.filter((id) => id !== vehicleId);
  }

  // Add to new garage
  if (!toGarage.vehicleIds.includes(vehicleId)) {
    toGarage.vehicleIds.push(vehicleId);
  }

  // Update vehicle's garageId
  vehicle.garageId = toGarageId;

  saveGarages(garages);
  saveVehicles(vehicles);
}

/* ───────────────────── Analyses ───────────────────── */

export function getAllAnalyses() {
  try {
    const raw = localStorage.getItem(KEYS.ANALYSES);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

export function getAnalysis(vehicleId) {
  const all = getAllAnalyses();
  return all[vehicleId] || null;
}

export function saveAnalysis(vehicleId, data) {
  try {
    const all = getAllAnalyses();
    all[vehicleId] = { ...data, analyzedAt: new Date().toISOString() };
    localStorage.setItem(KEYS.ANALYSES, JSON.stringify(all));
  } catch (err) {
    if (import.meta.env.DEV) console.error('[Storage] saveAnalysis failed:', err.message);
  }
}

/**
 * Re-key an analysis entry when a vehicle's ID changes (e.g. hydration from Supabase).
 * Preserves the analysis data under the new key so it isn't orphaned.
 */
export function rekeyAnalysis(oldId, newId) {
  if (!oldId || !newId || oldId === newId) return;
  try {
    const all = getAllAnalyses();
    if (all[oldId] && !all[newId]) {
      all[newId] = all[oldId];
      delete all[oldId];
      localStorage.setItem(KEYS.ANALYSES, JSON.stringify(all));
    }
  } catch {
    // best-effort
  }
}

/* ───────────────────── Service History ───────────────────── */

export function getServiceHistory(vehicleId) {
  const raw = localStorage.getItem(KEYS.SERVICE_HISTORY);
  const all = raw ? JSON.parse(raw) : [];
  if (vehicleId) return all.filter((e) => e.vehicleId === vehicleId);
  return all;
}

export function saveServiceHistory(entries) {
  localStorage.setItem(KEYS.SERVICE_HISTORY, JSON.stringify(entries));
}

export function addServiceEntry(vehicleId, entry) {
  const history = getServiceHistory();
  history.unshift({
    ...entry,
    vehicleId,
    id: `svc-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  });
  saveServiceHistory(history);

  // Also update vehicle lastService date
  const vehicle = getVehicle(vehicleId);
  if (vehicle && entry.date) {
    vehicle.lastService = entry.date;
    saveVehicle(vehicle);
  }

  return history;
}

export function removeServiceEntry(id) {
  const history = getServiceHistory().filter((e) => e.id !== id);
  saveServiceHistory(history);
  return history;
}

export function updateServiceEntry(id, fields) {
  const history = getServiceHistory();
  const idx = history.findIndex((e) => e.id === id);
  if (idx === -1) return history;
  history[idx] = { ...history[idx], ...fields };
  saveServiceHistory(history);
  return history;
}

/* ───────────────────── Chat Histories ───────────────────── */

function getAllChatHistories() {
  const raw = localStorage.getItem(KEYS.CHAT_HISTORIES);
  return raw ? JSON.parse(raw) : {};
}

export function getChatHistory(vehicleId) {
  const all = getAllChatHistories();
  return all[vehicleId] || [];
}

export function saveChatHistory(vehicleId, messages) {
  const all = getAllChatHistories();
  all[vehicleId] = messages;
  try {
    localStorage.setItem(KEYS.CHAT_HISTORIES, JSON.stringify(all));
  } catch (e) {
    if (import.meta.env.DEV) console.warn('[storage] Chat save hit quota, stripping photos and retrying');
    // Strip photo dataUrls from older messages to free space, keep latest 2
    const stripped = messages.map((m, i) => {
      if (m.photos && i < messages.length - 2) {
        return { ...m, photos: m.photos.map((p) => ({ ...p, dataUrl: '' })) };
      }
      return m;
    });
    all[vehicleId] = stripped;
    try {
      localStorage.setItem(KEYS.CHAT_HISTORIES, JSON.stringify(all));
    } catch {
      // Still too large — skip persistence, messages remain in React state
      if (import.meta.env.DEV) console.warn('[storage] Chat save failed even after stripping photos');
    }
  }
  lazyBackupSync(KEYS.CHAT_HISTORIES, _backupUserId);
}

/* ───────────────────── Auto Advisor (unchanged) ───────────────────── */

export function saveAutoAdvisorAnalysis(analysis) {
  const existing = getAutoAdvisorAnalyses();
  existing.push({ ...analysis, savedAt: new Date().toISOString() });
  localStorage.setItem(KEYS.AA_ANALYSES, JSON.stringify(existing));
  lazyBackupSync(KEYS.AA_ANALYSES, _backupUserId);
}

export function getAutoAdvisorAnalyses() {
  const raw = localStorage.getItem(KEYS.AA_ANALYSES);
  return raw ? JSON.parse(raw) : [];
}

export function clearAutoAdvisorAnalyses() {
  localStorage.removeItem(KEYS.AA_ANALYSES);
  lazyBackupSync(KEYS.AA_ANALYSES, _backupUserId);
}

export function removeAutoAdvisorAnalysis(savedAt) {
  const existing = getAutoAdvisorAnalyses();
  const filtered = existing.filter((a) => a.savedAt !== savedAt);
  localStorage.setItem(KEYS.AA_ANALYSES, JSON.stringify(filtered));
  lazyBackupSync(KEYS.AA_ANALYSES, _backupUserId);
  return filtered;
}

/* ───────────────────── Auto Advisor Checklist ───────────────────── */

export function getChecklistState(reportId) {
  const raw = localStorage.getItem(KEYS.AA_CHECKLIST);
  const all = raw ? JSON.parse(raw) : {};
  return all[reportId] || {};
}

export function toggleChecklistItem(reportId, itemIndex) {
  const raw = localStorage.getItem(KEYS.AA_CHECKLIST);
  const all = raw ? JSON.parse(raw) : {};
  if (!all[reportId]) all[reportId] = {};
  all[reportId][itemIndex] = !all[reportId][itemIndex];
  localStorage.setItem(KEYS.AA_CHECKLIST, JSON.stringify(all));
  lazyBackupSync(KEYS.AA_CHECKLIST, _backupUserId);
  return all[reportId];
}

/* ───────────────────── Value History (Trends) ───────────────────── */

export function getValueHistory(vehicleId) {
  const raw = localStorage.getItem(KEYS.VALUE_HISTORY);
  const all = raw ? JSON.parse(raw) : {};
  return all[vehicleId] || [];
}

export function addValueSnapshot(vehicleId, { marketValueLow, marketValueHigh, healthScore, date }) {
  const raw = localStorage.getItem(KEYS.VALUE_HISTORY);
  const all = raw ? JSON.parse(raw) : {};
  if (!all[vehicleId]) all[vehicleId] = [];
  all[vehicleId].push({
    marketValueLow,
    marketValueHigh,
    healthScore,
    date: date || new Date().toISOString(),
  });
  // Cap at 50 entries per vehicle
  if (all[vehicleId].length > 50) all[vehicleId] = all[vehicleId].slice(-50);
  localStorage.setItem(KEYS.VALUE_HISTORY, JSON.stringify(all));
}

/* ───────────────────── Settings ───────────────────── */

// Locale-aware unit default for new users. Existing users keep whatever's
// in their saved settings (this only fires on first run).
function defaultUnitsForLocale() {
  try {
    const lang = (navigator?.language || 'en-US').toUpperCase();
    const region = lang.includes('-') ? lang.split('-')[1] : '';
    // US, Liberia, Myanmar are the only countries that don't use km.
    return ['US', 'LR', 'MM'].includes(region) ? 'miles' : 'km';
  } catch { return 'miles'; }
}

const DEFAULT_SETTINGS = {
  profile: { name: 'Carsten Dietz', email: 'carsten@propermiles.com' },
  subscription: { plan: 'free', creditsUsed: 3, creditsTotal: 15 },
  preferences: { units: defaultUnitsForLocale(), currency: 'USD', notifyMaintenance: true, notifyMarket: false, showEvents: false, pushReminders: true, pushAnalysisComplete: true, pushMileageCheckin: true, pushEventCheckin: true, pushReferralAccepted: true },
};

export const PLAN_LIMITS = {
  free:       { maxVehicles: 1,        maxGarages: 1,        creditsTotal: 15,  canShare: false },
  enthusiast: { maxVehicles: 5,        maxGarages: 1,        creditsTotal: 100, canShare: false },
  collector:  { maxVehicles: 15,       maxGarages: 3,        creditsTotal: 250, canShare: false },
  showroom:   { maxVehicles: 50,       maxGarages: 5,        creditsTotal: 500, canShare: false },
  team:       { maxVehicles: Infinity, maxGarages: Infinity,  creditsTotal: 500, canShare: true  },
};

export function getSubscriptionLimits() {
  const settings = getSettings();
  const base = PLAN_LIMITS[settings.subscription.plan] || PLAN_LIMITS.free;
  const bonus = settings.bonusVehicles || 0;
  if (bonus > 0 && base.maxVehicles !== Infinity) {
    return { ...base, maxVehicles: base.maxVehicles + bonus };
  }
  return base;
}

/**
 * Count only the user's OWN vehicles (excludes shared/collaborator garage vehicles).
 * Use this for subscription limit checks instead of getVehicles().length.
 */
export function getOwnedVehicleCount() {
  const allGarages = getGarages();
  const collaboratorGarageIds = new Set(
    allGarages.filter(g => g.role === 'collaborator').map(g => g.id)
  );
  const allVehicles = getVehicles();
  // Count vehicles NOT in collaborator garages (includes orphaned vehicles with null/missing garageId)
  return allVehicles.filter(v => !collaboratorGarageIds.has(v.garageId)).length;
}

export function getSettings() {
  const raw = localStorage.getItem(KEYS.SETTINGS);
  if (raw) return JSON.parse(raw);
  // Lazy-init with defaults
  localStorage.setItem(KEYS.SETTINGS, JSON.stringify(DEFAULT_SETTINGS));
  return { ...DEFAULT_SETTINGS };
}

export function saveSettings(settings) {
  localStorage.setItem(KEYS.SETTINGS, JSON.stringify(settings));
  lazyBackupSync(KEYS.SETTINGS, _backupUserId);
}

export function updateSettings(fields) {
  const current = getSettings();
  const updated = { ...current, ...fields };
  // Deep merge for nested objects
  if (fields.profile) updated.profile = { ...current.profile, ...fields.profile };
  if (fields.subscription) updated.subscription = { ...current.subscription, ...fields.subscription };
  if (fields.preferences) updated.preferences = { ...current.preferences, ...fields.preferences };
  saveSettings(updated);
  return updated;
}

/**
 * Sync subscription data from a Supabase user row into localStorage settings.
 * Called after Stripe checkout success and on app load.
 */
export function syncSubscriptionFromSupabase(userRow) {
  if (!userRow) return;
  const tier = userRow.subscription_tier || 'free';
  const limits = PLAN_LIMITS[tier] || PLAN_LIMITS.free;
  const creditsRemaining = userRow.credits_remaining != null
    ? userRow.credits_remaining
    : limits.creditsTotal;
  const updates = {
    subscription: {
      plan: tier,
      creditsUsed: Math.max(0, limits.creditsTotal - creditsRemaining),
      creditsTotal: limits.creditsTotal,
      isClubOwner: !!userRow.is_club_owner,
      clubOwnerTier: userRow.club_owner_tier || null,
    },
  };
  // Sync bonus vehicles from referral system
  if (userRow.bonus_vehicles != null) {
    updates.bonusVehicles = userRow.bonus_vehicles;
  }
  updateSettings(updates);
}

/* ───────────────────── User Profile ───────────────────── */

export function getUserProfile() {
  const raw = localStorage.getItem(KEYS.USER_PROFILE);
  return raw ? JSON.parse(raw) : null;
}

export function saveUserProfile(profile) {
  const data = { ...profile, createdAt: profile.createdAt || new Date().toISOString() };
  localStorage.setItem(KEYS.USER_PROFILE, JSON.stringify(data));
  // Also sync name/email/phone into settings profile
  updateSettings({ profile: { name: `${data.firstName} ${data.lastName}`, email: data.email, phone: data.phone || '' } });
  return data;
}

export function hasUserProfile() {
  return !!localStorage.getItem(KEYS.USER_PROFILE);
}

/* ───────────────────── Mileage Logs ───────────────────── */

export function getMileageLogs(vehicleId) {
  const raw = localStorage.getItem(KEYS.MILEAGE_LOGS);
  const all = raw ? JSON.parse(raw) : {};
  return all[vehicleId] || [];
}

export function addMileageLog(vehicleId, entry) {
  const raw = localStorage.getItem(KEYS.MILEAGE_LOGS);
  const all = raw ? JSON.parse(raw) : {};
  if (!all[vehicleId]) all[vehicleId] = [];
  const logEntry = {
    ...entry,
    id: `ml-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    createdAt: new Date().toISOString(),
  };
  all[vehicleId].unshift(logEntry);
  localStorage.setItem(KEYS.MILEAGE_LOGS, JSON.stringify(all));
  lazyBackupSync(KEYS.MILEAGE_LOGS, _backupUserId);

  // Always update vehicle mileage + TMU flag
  if (entry.mileage) {
    const vehicle = getVehicle(vehicleId);
    if (vehicle) {
      vehicle.mileage = entry.mileage;
      vehicle.tmu = !!entry.tmu;
      vehicle.lastMileageUpdateAt = new Date().toISOString();
      saveVehicle(vehicle);
    }
  }

  return all[vehicleId];
}

/* ───────────────────── Dismissed Maintenance Items ───────────────────── */

export function getDismissedItems(vehicleId) {
  const raw = localStorage.getItem(KEYS.DISMISSED_ITEMS);
  const all = raw ? JSON.parse(raw) : {};
  return all[vehicleId] || [];
}

export function dismissMaintenanceItem(vehicleId, service, reason) {
  const raw = localStorage.getItem(KEYS.DISMISSED_ITEMS);
  const all = raw ? JSON.parse(raw) : {};
  if (!all[vehicleId]) all[vehicleId] = [];
  all[vehicleId].push({
    service,
    reason,
    dismissedAt: new Date().toISOString(),
  });
  localStorage.setItem(KEYS.DISMISSED_ITEMS, JSON.stringify(all));
  lazyBackupSync(KEYS.DISMISSED_ITEMS, _backupUserId);
  return all[vehicleId];
}

/* ───────────────────── DIY Guides (Persistence) ───────────────────── */

/**
 * Guide key format: `${vehicleId}::${serviceName}`
 * Each saved guide stores the full AI output, challenge history, step progress, and metadata.
 */

function guideKey(vehicleId, service) {
  return `${vehicleId}::${service}`;
}

export function getSavedGuide(vehicleId, service) {
  const raw = localStorage.getItem(KEYS.DIY_GUIDES);
  const all = raw ? JSON.parse(raw) : {};
  return all[guideKey(vehicleId, service)] || null;
}

export function getAllSavedGuides(vehicleId) {
  const raw = localStorage.getItem(KEYS.DIY_GUIDES);
  const all = raw ? JSON.parse(raw) : {};
  if (!vehicleId) return all;
  const result = {};
  Object.entries(all).forEach(([k, v]) => {
    if (k.startsWith(vehicleId + '::')) result[k] = v;
  });
  return result;
}

export function saveGuide(vehicleId, service, guideData) {
  const raw = localStorage.getItem(KEYS.DIY_GUIDES);
  const all = raw ? JSON.parse(raw) : {};
  const key = guideKey(vehicleId, service);
  const existing = all[key];

  all[key] = {
    vehicleId,
    service,
    guide: guideData,
    challenges: existing?.challenges || [],
    completedSteps: existing?.completedSteps || [],
    lastAccessedAt: new Date().toISOString(),
    createdAt: existing?.createdAt || new Date().toISOString(),
  };

  localStorage.setItem(KEYS.DIY_GUIDES, JSON.stringify(all));
  lazyBackupSync(KEYS.DIY_GUIDES, _backupUserId);
  return all[key];
}

export function updateGuideProgress(vehicleId, service, completedSteps) {
  const raw = localStorage.getItem(KEYS.DIY_GUIDES);
  const all = raw ? JSON.parse(raw) : {};
  const key = guideKey(vehicleId, service);
  if (!all[key]) return null;

  all[key].completedSteps = completedSteps;
  all[key].lastAccessedAt = new Date().toISOString();

  localStorage.setItem(KEYS.DIY_GUIDES, JSON.stringify(all));
  lazyBackupSync(KEYS.DIY_GUIDES, _backupUserId);
  return all[key];
}

export function addChallengeToGuide(vehicleId, service, challenge) {
  const raw = localStorage.getItem(KEYS.DIY_GUIDES);
  const all = raw ? JSON.parse(raw) : {};
  const key = guideKey(vehicleId, service);
  if (!all[key]) return null;

  const entry = {
    ...challenge,
    id: `ch-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    date: new Date().toISOString(),
  };
  all[key].challenges.push(entry);
  all[key].lastAccessedAt = new Date().toISOString();

  localStorage.setItem(KEYS.DIY_GUIDES, JSON.stringify(all));
  lazyBackupSync(KEYS.DIY_GUIDES, _backupUserId);
  return all[key];
}

export function applyChallengeCorrection(vehicleId, service, stepNumber, correctedStep) {
  const raw = localStorage.getItem(KEYS.DIY_GUIDES);
  const all = raw ? JSON.parse(raw) : {};
  const key = guideKey(vehicleId, service);
  if (!all[key]) return null;

  // Replace the step in the guide
  const guide = all[key].guide;
  const stepIdx = guide.steps.findIndex((s) => s.number === stepNumber);
  if (stepIdx !== -1) {
    guide.steps[stepIdx] = {
      ...correctedStep,
      userCorrected: true,
      correctedAt: new Date().toISOString(),
    };
  }
  all[key].guide = guide;
  all[key].lastAccessedAt = new Date().toISOString();

  localStorage.setItem(KEYS.DIY_GUIDES, JSON.stringify(all));
  lazyBackupSync(KEYS.DIY_GUIDES, _backupUserId);
  return all[key];
}

export function deleteGuide(vehicleId, service) {
  const raw = localStorage.getItem(KEYS.DIY_GUIDES);
  const all = raw ? JSON.parse(raw) : {};
  delete all[guideKey(vehicleId, service)];
  localStorage.setItem(KEYS.DIY_GUIDES, JSON.stringify(all));
  lazyBackupSync(KEYS.DIY_GUIDES, _backupUserId);
}

/* ───────────────────── Internal Notes (Team Garages) ───────────────────── */

export function getInternalNotes(vehicleId) {
  const vehicle = getVehicle(vehicleId);
  return vehicle?.internalNotes || null;
}

export function saveInternalNotes(vehicleId, content, userName, userEmail) {
  const vehicle = getVehicle(vehicleId);
  if (!vehicle) return null;

  const now = new Date().toISOString();
  const existing = vehicle.internalNotes || { history: [] };

  // Push current content to history before overwriting (if there was previous content)
  if (existing.content) {
    existing.history = [
      {
        content: existing.content,
        updatedBy: existing.lastUpdatedBy,
        updatedByName: existing.lastUpdatedByName,
        updatedAt: existing.lastUpdatedAt,
      },
      ...existing.history,
    ].slice(0, 10); // Cap at 10 history entries
  }

  vehicle.internalNotes = {
    content,
    lastUpdatedBy: userEmail,
    lastUpdatedByName: userName,
    lastUpdatedAt: now,
    history: existing.history,
  };

  saveVehicle(vehicle);
  return vehicle.internalNotes;
}

/* ───────────────────── Legal Acceptance ───────────────────── */

/**
 * Legal acceptances are stored inside the user profile:
 * profile.legalAcceptances = {
 *   termsVersion, termsAcceptedAt,
 *   privacyVersion, privacyAcceptedAt,
 *   safetyVersion, safetyAcceptedAt,
 * }
 */

export function getLegalAcceptances() {
  const profile = getUserProfile();
  return profile?.legalAcceptances || {};
}

export function acceptLegalDocument(type, version) {
  const profile = getUserProfile();
  if (!profile) return null;

  const acceptances = profile.legalAcceptances || {};

  if (type === 'terms') {
    acceptances.termsVersion = version;
    acceptances.termsAcceptedAt = new Date().toISOString();
  } else if (type === 'privacy') {
    acceptances.privacyVersion = version;
    acceptances.privacyAcceptedAt = new Date().toISOString();
  } else if (type === 'safety') {
    acceptances.safetyVersion = version;
    acceptances.safetyAcceptedAt = new Date().toISOString();
  }

  profile.legalAcceptances = acceptances;
  localStorage.setItem(KEYS.USER_PROFILE, JSON.stringify(profile));
  return acceptances;
}

export function hasAcceptedLegal(type, requiredVersion) {
  const acceptances = getLegalAcceptances();
  if (type === 'terms') return acceptances.termsVersion === requiredVersion;
  if (type === 'privacy') return acceptances.privacyVersion === requiredVersion;
  if (type === 'safety') return acceptances.safetyVersion === requiredVersion;
  return false;
}

/* ───────────────────── Reminders ───────────────────── */

export function getReminders(vehicleId) {
  const raw = localStorage.getItem(KEYS.REMINDERS);
  const all = raw ? JSON.parse(raw) : [];
  if (vehicleId) return all.filter((r) => r.vehicleId === vehicleId);
  return all;
}

export function saveReminders(reminders) {
  localStorage.setItem(KEYS.REMINDERS, JSON.stringify(reminders));
}

export function addReminder(reminder) {
  const all = getReminders();
  const entry = {
    ...reminder,
    id: reminder.id || crypto.randomUUID(),
    status: 'pending',
    completedAt: null,
    createdAt: new Date().toISOString(),
  };
  all.push(entry);
  saveReminders(all);
  return entry;
}

export function updateReminder(id, fields) {
  const all = getReminders();
  const idx = all.findIndex((r) => r.id === id);
  if (idx === -1) return all;
  all[idx] = { ...all[idx], ...fields };
  saveReminders(all);
  return all;
}

export function completeReminder(id) {
  return updateReminder(id, { status: 'completed', completedAt: new Date().toISOString() });
}

export function removeReminder(id) {
  const all = getReminders().filter((r) => r.id !== id);
  saveReminders(all);
  return all;
}

/* ───────────────────── Club Memberships ───────────────────── */

export function getClubMemberships() {
  try { return JSON.parse(localStorage.getItem(KEYS.CLUB_MEMBERSHIPS)) || []; }
  catch { return []; }
}

export function saveClubMemberships(memberships) {
  localStorage.setItem(KEYS.CLUB_MEMBERSHIPS, JSON.stringify(memberships));
}

/* ───────────────────── Cached Events ───────────────────── */

export function getCachedEvents() {
  try { return JSON.parse(localStorage.getItem(KEYS.CACHED_EVENTS)) || []; }
  catch { return []; }
}

export function saveCachedEvents(events) {
  localStorage.setItem(KEYS.CACHED_EVENTS, JSON.stringify(events));
}

/* ───────────────────── Reset ───────────────────── */

export function clearAll() {
  Object.values(KEYS).forEach((k) => localStorage.removeItem(k));
}
